import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import { ArrowDirection, GalleryProps } from '@/components/common/Gallery/types';
import {
  Container,
  GalleryButton,
  SliderDot,
  SliderDotContainer,
  SliderDots,
  SliderItem,
  SliderThumbnail,
  SliderThumbnailItem,
  SliderWrapper,
} from '@/components/common/Gallery/styles';
import { useDrag } from '@/hooks/useDrag';
import { GalleryItem } from '@/components/common/Gallery/GalleryItem';
import { GalleryThumbnail } from '@/components/common/Gallery/GalleryThumbnail';
import Icon from '@/units/Icon';
import { Arrow } from '@/components/common/Gallery/Arrow';
import { AlternativeArrow } from './AlternativeArrow';

const defaultSettings = {
  thumbnails: true,
  fullScreen: true,
  dots: true,
  imageHeight: 200,
  thumbnailSize: 52,
  thumbnailBorderWidth: 1,
  thumbnailsGap: 10,
  thumbnailsMarginRight: 0,
  dotsColor: 'white',
  thumbnailIconBackground: '#ffffff',
  thumbnailBorderColor: 'transparent',
  activeArrows: false,
  arrowStyle: {},
  fullscreenByClick: false,
  alternativeArrows: false,
  isNotesGallery: false,
};

export const Gallery: FC<GalleryProps> = ({
  items,
  onGalleryItemClick,
  settings = defaultSettings,
}) => {
  const gallerySettings = { ...defaultSettings, ...settings };
  const [slideIndex, setSlideIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const isDragging = useDrag();
  const [galleryWidth, setGalleryWidth] = useState(0);
  const [inFullScreen, setInFullScreen] = useState(false);

  const containerRef = useRef(null);
  const sliderRef = useRef(null);
  const sliderThumbnailRef = useRef(null);

  const thumbnailsLength =
    items.length *
      (gallerySettings.thumbnailSize +
        gallerySettings.thumbnailsGap +
        gallerySettings.thumbnailBorderWidth * 2) -
    10;
  const thumbnailsRowWidth =
    galleryWidth && thumbnailsLength < galleryWidth ? `${thumbnailsLength}px` : 'auto';

  const setSlide = (index: number) => {
    if (isChanging || index === slideIndex || index < 0 || index >= items.length) return;

    setSlideIndex(index);

    if (sliderRef.current && sliderRef.current.slickGoTo) {
      try {
        sliderRef.current.slickGoTo(index);
      } catch (error) {
        console.warn('Error navigating main slider:', error);
      }
    }

    if (sliderThumbnailRef.current && sliderThumbnailRef.current.slickGoTo) {
      try {
        sliderThumbnailRef.current.slickGoTo(index);
      } catch (error) {
        console.warn('Error navigating thumbnail slider:', error);
      }
    }
  };

  const navigateToNextSlide = () => {
    const nextIndex = slideIndex === items.length - 1 ? 0 : slideIndex + 1;
    setSlide(nextIndex);
  };

  const navigateToPrevSlide = () => {
    const prevIndex = slideIndex === 0 ? items.length - 1 : slideIndex - 1;
    setSlide(prevIndex);
  };

  const toggleFullscreen = (ref) => {
    if (!document.fullscreenElement) {
      // TODO: consider cross-browser compatibility
      if (ref.current.requestFullscreen) {
        ref.current
          .requestFullscreen()
          .then(() => {
            setInFullScreen(true);
          })
          .catch((err) => {
            setInFullScreen(false);
            alert(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
          });
      }
    } else if (document.exitFullscreen) {
      setInFullScreen(false);
      document.exitFullscreen();
    }
  };

  useLayoutEffect(() => {
    const { width } = containerRef.current.getBoundingClientRect();
    setGalleryWidth(width);
  }, [inFullScreen]);

  const handleFullScreenExit = () => {
    if (!document.fullscreenElement) {
      setInFullScreen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreenExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenExit);
    };
  }, []);

  // Add keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (inFullScreen) {
        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            navigateToPrevSlide();
            break;
          case 'ArrowRight':
            event.preventDefault();
            navigateToNextSlide();
            break;
          case 'Escape':
            event.preventDefault();
            if (document.exitFullscreen) {
              setInFullScreen(false);
              document.exitFullscreen();
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [inFullScreen, slideIndex, items.length]);

  return (
    <Container ref={containerRef}>
      {galleryWidth && (
        <>
          {gallerySettings.fullScreen && !inFullScreen && (
            <GalleryButton
              style={{
                position: 'absolute',
                top: `${gallerySettings.imageHeight - 20 - 48}px`,
                right: '20px',
              }}
              onClick={() => toggleFullscreen(containerRef)}
            >
              <Icon.FullScreen />
            </GalleryButton>
          )}
          {inFullScreen && (
            <GalleryButton
              style={{ position: 'fixed', top: `20px`, right: '20px' }}
              onClick={() => toggleFullscreen(containerRef)}
            >
              <Icon.Close
                style={{ color: 'white', width: '100%', height: '100%' }}
                fontSize="large"
              />
            </GalleryButton>
          )}
          <SliderWrapper>
            <Slider
              ref={sliderRef}
              arrows={inFullScreen || gallerySettings.activeArrows}
              nextArrow={
                gallerySettings.alternativeArrows ? (
                  <AlternativeArrow
                    direction={ArrowDirection.Right}
                    style={gallerySettings.arrowStyle}
                  />
                ) : (
                  <Arrow direction={ArrowDirection.Right} style={gallerySettings.arrowStyle} />
                )
              }
              prevArrow={
                gallerySettings.alternativeArrows ? (
                  <AlternativeArrow
                    direction={ArrowDirection.Left}
                    style={gallerySettings.arrowStyle}
                  />
                ) : (
                  <Arrow direction={ArrowDirection.Left} style={gallerySettings.arrowStyle} />
                )
              }
              dots={false}
              infinite={items.length > 1}
              beforeChange={() => {
                setIsChanging(true);
              }}
              afterChange={(i) => {
                if (slideIndex !== i) setSlideIndex(i);
                if (sliderThumbnailRef.current) {
                  if (thumbnailsRowWidth === 'auto') {
                    sliderThumbnailRef.current.slickGoTo(i);
                  } else {
                    // For fixed width thumbnails, ensure the active thumbnail is visible
                    const thumbnailSlider = sliderThumbnailRef.current;
                    const totalSlides = items.length;
                    if (totalSlides > 1) {
                      thumbnailSlider.slickGoTo(i);
                    }
                  }
                }
                setIsChanging(false);
              }}
            >
              {items.map((item, index) => (
                <SliderItem
                  key={item.toString()}
                  height={inFullScreen ? '75vh' : gallerySettings.imageHeight}
                  padding={
                    inFullScreen && gallerySettings.isNotesGallery && items.length > 1
                      ? `10vh 10vw 0 10vw`
                      : 0
                  }
                >
                  <GalleryItem
                    item={item}
                    index={index}
                    onPointerUp={() => {
                      if (gallerySettings.fullscreenByClick && !inFullScreen)
                        toggleFullscreen(containerRef);
                      if (!isDragging && onGalleryItemClick) onGalleryItemClick();
                    }}
                    inFullScreen={inFullScreen}
                    isNotesGallery={gallerySettings.isNotesGallery}
                  />
                </SliderItem>
              ))}
            </Slider>
          </SliderWrapper>
          {gallerySettings.thumbnails ? (
            <SliderThumbnail
              margin={
                thumbnailsRowWidth === 'auto'
                  ? `0 ${gallerySettings.thumbnailsMarginRight}px 0 0`
                  : 'auto'
              }
              width={thumbnailsRowWidth}
            >
              <Slider
                ref={sliderThumbnailRef}
                arrows={false}
                dots={false}
                draggable
                swipeToSlide
                variableWidth
                infinite={items.length > 1}
                beforeChange={() => {
                  setIsChanging(true);
                }}
                afterChange={() => {
                  setIsChanging(false);
                }}
              >
                {items.map((item, index) => (
                  <SliderThumbnailItem
                    key={index}
                    thumbnailBorderWidth={gallerySettings.thumbnailBorderWidth}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSlide(index);
                    }}
                  >
                    <GalleryThumbnail
                      item={item}
                      active={index === slideIndex}
                      thumbnailsGap={gallerySettings.thumbnailsGap}
                      onPointerUp={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSlide(index);
                      }}
                      thumbnailIconBackground={gallerySettings.thumbnailIconBackground}
                      thumbnailSize={gallerySettings.thumbnailSize}
                      thumbnailBorderColor={gallerySettings.thumbnailBorderColor}
                      isNotesGallery={gallerySettings.isNotesGallery}
                      inFullScreen={inFullScreen}
                    />
                  </SliderThumbnailItem>
                ))}
              </Slider>
            </SliderThumbnail>
          ) : null}
          {gallerySettings.dots && (
            <SliderDots style={{ marginBottom: inFullScreen ? '20px' : 0 }}>
              {items.map((item, index) => (
                <SliderDotContainer
                  key={index}
                  onPointerUp={() => {
                    setSlide(index);
                  }}
                >
                  <SliderDot active={index === slideIndex} dotsColor={gallerySettings.dotsColor} />
                </SliderDotContainer>
              ))}
            </SliderDots>
          )}
        </>
      )}
    </Container>
  );
};
